import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  computed,
  inject,
  input,
} from '@angular/core';
import { FieldTree } from '@angular/forms/signals';
import { ChecklistService } from '../../../checklist/services/checklist.service';
import { ChecklistHttpService } from '../../../../services/checklist-http.service';
import { OfflineStorageService } from '../../../../services/offline/offline-storage.service';
import { FieldMetadata } from '../field.model';
import { FileRef, FileMetadata } from '../file-upload/file-upload.types';
import { FileUploadComponent } from '../file-upload/file-upload.component';
import { ImageThumbnailComponent } from './image-thumbnail.component';
import { ImagePreviewModalComponent } from './image-preview-modal.component';
import { AppButton, ButtonType } from '../../button/button.component';

@Component({
  selector: 'app-image-upload',
  standalone: true,
  imports: [
    AppButton,
    FileUploadComponent,
    ImageThumbnailComponent,
    ImagePreviewModalComponent,
  ],
  templateUrl: './image-upload.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageUploadComponent {
  readonly field = input.required<FieldTree<FileRef[]>>();
  readonly label = input('');
  readonly hideLabel = input(false);
  readonly maxFiles = input(5);
  readonly metadata = input<FieldMetadata>();

  readonly accept = 'image/jpeg,image/png,image/heic,image/heif,image/webp';
  readonly helperText =
    'Up to 5 pictures, max. 10 MB each. Supported formats: PNG, JPG, WEBP, HEIC.';

  previewFileId: string | null = null;

  readonly promoteIfPreview = (): Promise<void> =>
    this.checklistService.promoteIfPreviewAsync();

  private readonly checklistService = inject(ChecklistService);

  // Selecting a file stores its blob in IndexedDB before beforePersist runs, so
  // an isolated preview shows a disabled button, never the real file control.
  protected readonly isIsolatedPreview =
    this.checklistService.isIsolatedPreview;
  protected readonly isReadOnly = computed(
    () => this.state().disabled() || this.isIsolatedPreview()
  );
  protected readonly ButtonType = ButtonType;
  private readonly http = inject(ChecklistHttpService);
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private optimisticDeleteFileOrder: string[] | null = null;

  protected readonly state = computed(() => this.field()());

  get checklistId(): string {
    return this.checklistService.checklist()?.id ?? '';
  }

  get itemId(): string {
    return this.metadata()?.customInput?.itemId ?? '';
  }

  get customInputId(): string {
    return this.metadata()?.customInput?.id ?? '';
  }

  get isRequired(): boolean {
    return this.state().required();
  }

  get isReadonly(): boolean {
    return this.state().disabled();
  }

  get files(): FileRef[] {
    return this.state().value() ?? [];
  }

  get previewFileIds(): string[] {
    return this.files.map(file => file.id);
  }

  onFileUploaded(metadata: FileMetadata): void {
    const ref: FileRef = {
      id: metadata.id,
      fileName: metadata.fileName,
      contentType: metadata.contentType,
      originalFileName: metadata.originalFileName,
    };
    this.setFiles([...this.files, ref]);
    this.cdr.markForCheck();
  }

  onFileRemoved(fileId: string): void {
    this.setFiles(this.files.filter(f => f.id !== fileId));
    this.cdr.markForCheck();
  }

  onUploadError(_: { file: File; error: unknown }): void {
    void _;
  }

  removeFile(fileId: string): Promise<void> {
    return this.removeFileAsync(fileId).catch(() => undefined);
  }

  private async removeFileAsync(fileId: string): Promise<void> {
    const fileRef = this.files.find(file => file.id === fileId);
    if (!fileRef) {
      return;
    }
    const visibleFileOrder = this.files.map(file => file.id);
    const fileIndex = this.files.findIndex(file => file.id === fileId);

    const queuedUploadRemoval =
      await this.offlineStorage.removeQueuedUploadByFileId(fileId);
    if (queuedUploadRemoval === 'removed') {
      await this.evictCachedBlob(fileId);
      this.removeLocalFile(fileId);
      return;
    }

    const fileOrder = this.buildDeleteFileOrder(visibleFileOrder);
    await this.offlineStorage.addAction(
      this.http.deleteFile,
      this.checklistId,
      this.itemId,
      fileId,
      {
        customInputId: this.customInputId,
        fileRef,
        fileIndex,
        fileOrder,
      }
    );
    this.optimisticDeleteFileOrder = fileOrder;
    if (queuedUploadRemoval !== 'in-flight') {
      await this.evictCachedBlob(fileId);
    }
    this.removeLocalFile(fileId);
  }

  private buildDeleteFileOrder(visibleFileOrder: string[]): string[] {
    if (!this.optimisticDeleteFileOrder) {
      return visibleFileOrder;
    }

    const mergedFileOrder = [...this.optimisticDeleteFileOrder];
    for (const fileId of visibleFileOrder) {
      if (!mergedFileOrder.includes(fileId)) {
        mergedFileOrder.push(fileId);
      }
    }
    return mergedFileOrder;
  }

  private async evictCachedBlob(fileId: string): Promise<void> {
    try {
      await this.offlineStorage.evictBlob(fileId);
    } catch {
      // Blob cleanup is best effort, and the queued action owns server state.
    }
  }

  private removeLocalFile(fileId: string): void {
    this.setFiles(this.files.filter(f => f.id !== fileId));
    this.cdr.markForCheck();
  }

  private setFiles(files: FileRef[]): void {
    this.state().value.set(files);
    this.state().markAsDirty();
    this.state().markAsTouched();
    this.checklistService.updateCustomInputPayloadLocally(
      this.itemId,
      this.customInputId,
      { files }
    );
  }

  openPreview(fileId: string): void {
    this.previewFileId = fileId;
    this.cdr.markForCheck();
  }

  showPreviewFile(fileId: string): void {
    this.previewFileId = fileId;
    this.cdr.markForCheck();
  }

  closePreview(): void {
    this.previewFileId = null;
    this.cdr.markForCheck();
  }
}
